import { Card } from "primereact/card";
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { RootStoreContext } from "../../../app/stores/rootStore";
import api from "../../../app/api/agent";

const ProjectDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const rootStore = useContext(RootStoreContext);
  const { selectedProject } = rootStore.projectStore;

  const [stats, setStats] = useState({
    geneName: '',
    targetName: '',
    compoundCount: 0,
    haCount: 0,
    hasPostPortfolio: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const project = selectedProject || { id: parseInt(id) };

        // 1. Target Name from project
        const targetName = project.targetName || project.target || '';

        // 2. Compound count
        let compoundCount = 0;
        try {
          const compResp = await api.requests.post('/Compound/search', {
            projectIds: [project.id],
            skip: 0,
            take: 1
          });
          compoundCount = compResp?.data?.total || compResp?.data?.data?.length || 0;
        } catch (e) {
          console.warn('Failed to fetch compound count:', e);
        }

        // 3. HA count
        let haCount = 0;
        try {
          const haResp = await api.requests.post('/HA/Search', {
            projectId: project.id,
            skip: 0,
            take: 1
          });
          haCount = haResp?.data?.total || haResp?.data?.data?.length || 0;
        } catch (e) {
          console.warn('Failed to fetch HA count:', e);
        }

        // 4. Gene name — try from project association
        let geneName = '';
        try {
          const geneResp = await api.requests.post('/Gen/Search', {
            projectIds: [project.id],
            skip: 0,
            take: 10
          });
          const genes = geneResp?.data?.data || [];
          if (genes.length > 0) {
            geneName = genes[0]?.name || genes[0]?.geneName || genes[0]?.accessionNumber || '';
          }
        } catch (e) {
          console.warn('Failed to fetch genes:', e);
        }

        // 5. Post-Portfolio
        const hasPostPortfolio = project.indEnabled || project.currentStage === 'IND' || false;

        setStats({ geneName, targetName, compoundCount, haCount, hasPostPortfolio });
      } catch (err) {
        console.error('Error fetching project dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [id, selectedProject]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const header = null;

  // Build nav-link paths: add projectId as query param for global pages
  const projectQuery = `projectId=${id}`;

  const cards = [
    {
      title: "Genes",
      subtitle: stats.geneName || '',
      link: `/d/gene/?${projectQuery}`,
      disabled: false,
    },
    {
      title: "Targets",
      subtitle: stats.targetName || '',
      link: `/d/target/?${projectQuery}`,
      disabled: false,
    },
    {
      title: "Screens",
      subtitle: stats.compoundCount > 0 ? `(${stats.compoundCount} compounds)` : '',
      link: `/d/screen/?${projectQuery}`,
      disabled: false,
    },
    {
      title: "HAs",
      subtitle: stats.haCount > 0 ? `(${stats.haCount} HAs)` : '',
      link: `/d/ha/?${projectQuery}`,
      disabled: false,
    },
    {
      title: "Portfolio",
      subtitle: "Project Information",
      link: `information/`,
      disabled: false,
    },
    {
      title: "Post-Portfolio",
      subtitle: stats.hasPostPortfolio ? 'Post-Portfolio stage active' : 'Not in post-portfolio',
      link: `/d/post-portfolio/?${projectQuery}`,
      disabled: !stats.hasPostPortfolio,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#007ad9' }}></i>
      </div>
    );
  }

  return (
    <div className="flex flex-column w-full">
      <h2 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.3rem' }}>
        {selectedProject?.projectName || 'Project'} Dashboard
      </h2>
      <div className="flex flex-column">
        <div className="flex justify-content-center gap-3 mb-3">
          {cards.slice(0, 3).map((card, idx) => (
            <div className="flex" key={idx}>
              <div
                onClick={() => !card.disabled && handleNavigate(card.link)}
                style={{
                  cursor: card.disabled ? 'not-allowed' : 'pointer',
                  opacity: card.disabled ? 0.5 : 1,
                  textDecoration: 'none',
                }}
              >
                <Card
                  title={card.title}
                  subTitle={card.subtitle}
                  style={{ width: "18em" }}
                  header={header}
                >
                  <p className="p-m-0" style={{ lineHeight: "1.5" }}>
                    View {card.title} &nbsp;
                    <i className="pi pi-arrow-right"></i>
                  </p>
                </Card>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-content-center gap-3">
          {cards.slice(3).map((card, idx) => (
            <div className="flex" key={idx + 3}>
              <div
                onClick={() => !card.disabled && handleNavigate(card.link)}
                style={{
                  cursor: card.disabled ? 'not-allowed' : 'pointer',
                  opacity: card.disabled ? 0.5 : 1,
                  textDecoration: 'none',
                }}
              >
                <Card
                  title={card.title}
                  subTitle={card.subtitle}
                  style={{ width: "18em" }}
                  header={header}
                >
                  <p className="p-m-0" style={{ lineHeight: "1.5" }}>
                    View {card.title} &nbsp;
                    <i className="pi pi-arrow-right"></i>
                  </p>
                </Card>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;
